import { useState, useEffect } from 'react';
import { Menu, X, Home, Building, Users, Construction, Briefcase, BrickWall } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const navLinks = [
  { href: '#home', label: 'Home', icon: Home },
  { href: '#services', label: 'Services', icon: Building },
  { href: '#projects', label: 'Projects', icon: Construction },
  { href: '#testimonials', label: 'Testimonials', icon: Users },
  { href: '#quotation', label: 'Quotation', icon: Briefcase },
  { href: '#about', label: 'About', icon: BrickWall }
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Update active section based on scroll position
      const sections = navLinks.map(link => link.href.substring(1));
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (current) {
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = (e, href) => {
    e.preventDefault();
    setIsOpen(false);

    if (href.startsWith('#')) {
      // Internal section navigation
      const targetElement = document.querySelector(href);
      if (targetElement) {
        const navHeight = 80;
        const elementPosition = targetElement.offsetTop - navHeight;
        
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }
    } else {
      // Page navigation
      navigate(href);
    }
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        isScrolled 
          ? 'bg-white shadow-lg' 
          : 'bg-gradient-to-b from-blue-600/90 to-blue-500/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo and Brand */}
          <div className="flex items-center gap-3 group">
            <div className="w-11 h-11 relative overflow-hidden rounded-xl 
              bg-white p-2 shadow-lg transform 
              group-hover:scale-105 group-hover:shadow-xl 
              transition-all duration-300"
            >
              <img 
                src="/assets/logo.svg" 
                alt="Urbanwall Projects" 
                className="w-full h-full object-contain
                  group-hover:scale-110 transition-transform duration-300"
              />
            </div>
            <div>
              <h1 className={`text-xl font-bold leading-none tracking-tight
                ${isScrolled ? 'text-gray-900' : 'text-white'}`}
              >
                Urbanwall
              </h1>
              <p className={`text-sm font-medium mt-0.5
                ${isScrolled ? 'text-blue-600' : 'text-white/90'}`}
              >
                Projects
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {navLinks.map(({ href, label, icon: Icon }) => (
              <a
                key={href}
                href={href}
                onClick={(e) => handleLinkClick(e, href)}
                className={`
                  px-4 py-2.5 rounded-xl flex items-center gap-2
                  transition-all duration-300 text-sm font-medium
                  hover:scale-105
                  ${activeSection === href.substring(1)
                    ? isScrolled
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-blue-600 shadow-md'
                    : isScrolled 
                      ? 'text-gray-700 hover:bg-gray-100' 
                      : 'text-white hover:bg-white/20'
                  }
                `}
              >
                <Icon className={`w-4 h-4 ${
                  activeSection === href.substring(1) && !isScrolled
                    ? 'text-blue-600'
                    : ''
                }`} />
                <span>{label}</span>
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2.5 rounded-xl transition-all duration-300
              hover:scale-105
              ${isScrolled 
                ? 'text-gray-600 hover:bg-gray-100' 
                : 'text-white hover:bg-white/20'
              }`}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`
            lg:hidden fixed inset-x-0 top-20 bg-white
            transition-all duration-300 ease-in-out border-t border-gray-100
            shadow-lg
            ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}
            z-[99]
          `}
        >
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div className="max-w-7xl mx-auto divide-y divide-gray-100">
              {navLinks.map(({ href, label, icon: Icon }) => (
                <a
                  key={href}
                  href={href}
                  onClick={(e) => handleLinkClick(e, href)}
                  className={`
                    flex items-center gap-4 p-4 transition-all duration-300
                    hover:bg-gray-50
                    ${activeSection === href.substring(1)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600'
                    }
                  `}
                >
                  <div className={`
                    p-2.5 rounded-xl transition-all duration-300
                    ${activeSection === href.substring(1) 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-500'
                    }
                  `}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium">{label}</div>
                    <div className="text-xs text-gray-500">
                      {getMenuDescription(label)}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[90] lg:hidden"
          onClick={() => setIsOpen(false)}
          style={{ top: '80px' }}
        />
      )}
    </nav>
  );
};

// Helper function to get menu descriptions
const getMenuDescription = (label) => {
  const descriptions = {
    'Home': 'Back to main page',
    'Services': 'Explore our offerings',
    'Projects': 'View our portfolio',
    'Testimonials': 'Client feedback',
    'Quotation': 'Request pricing',
    'About': 'Learn about us'
  };
  return descriptions[label] || '';
};

export default Navbar;
